import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../services/api';
import type { UserDetail } from '../services/types';
import { formatDate, formatMoney, subStatusClass, subStatusLabel } from '../utils/format';

type Tab = 'overview' | 'payments' | 'vpn' | 'promos' | 'invites';

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      const res = await api.getUser(Number(userId));
      if (res.data) setUser(res.data);
      else setError(res.error?.message ?? 'Пользователь не найден');
    })();
  }, [userId]);

  if (error) return <p style={{ color: 'var(--danger)' }}>{error}</p>;
  if (!user) return <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'payments', label: `Платежи (${user.payments.length})` },
    { id: 'vpn', label: `VPN (${user.vpn_keys.length})` },
    { id: 'promos', label: `Промо (${user.promos.length})` },
    { id: 'invites', label: `Инвайты (${user.invites_owned.length})` },
  ];

  return (
    <div>
      <Link to="/users" style={{ fontSize: '0.875rem' }}>
        ← К списку
      </Link>
      <h1 style={{ marginTop: '0.5rem' }}>
        {user.first_name ?? 'User'} #{user.user_id}
        {user.username ? ` @${user.username}` : ''}
      </h1>

      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid-stats">
          <InfoCard label="Подписка">
            <span className={subStatusClass(user.subscription_status)}>
              {subStatusLabel(user.subscription_status)}
            </span>
          </InfoCard>
          <InfoCard label="Tier">{user.subscription_tier ?? '—'}</InfoCard>
          <InfoCard label="До">{formatDate(user.subscription_until)}</InfoCard>
          <InfoCard label="VPN ключей">{user.active_keys_count}</InfoCard>
          <InfoCard label="Язык">{user.language}</InfoCard>
          <InfoCard label="Telegram Premium">{user.is_premium ? 'Да' : 'Нет'}</InfoCard>
          <InfoCard label="Уведомления об истечении">
            {user.notify_expiry ? 'Включены' : 'Выключены'}
          </InfoCard>
        </div>
      )}

      {tab === 'payments' && (
        <Table
          headers={['ID', 'Период', 'Tier', 'Дней', 'Сумма', 'Статус', 'Дата']}
          rows={user.payments.map((p) => [
            p.id,
            p.period,
            p.subscription_tier ?? '—',
            p.days,
            formatMoney(p.amount),
            p.status,
            formatDate(p.created_at),
          ])}
          empty="Нет платежей"
        />
      )}

      {tab === 'vpn' && (
        <Table
          headers={['ID', 'Сервер', 'Client ID', 'Статус', 'Создан']}
          rows={user.vpn_keys.map((k) => [
            <Link key={`id-${k.id}`} to={`/vpn/${k.id}`}>
              {k.id}
            </Link>,
            k.server_country.toUpperCase(),
            k.client_id,
            k.status,
            formatDate(k.created_at),
          ])}
          empty="Нет ключей"
        />
      )}

      {tab === 'promos' && (
        <Table
          headers={['Код', 'Использован']}
          rows={user.promos.map((p) => [p.code, formatDate(p.used_at)])}
          empty="Промокоды не использовались"
        />
      )}

      {tab === 'invites' && (
        <>
          <h3>Созданные инвайты</h3>
          <Table
            headers={['Код', 'Дней', 'Tier', 'Статус', 'Кем использован', 'Создан']}
            rows={user.invites_owned.map((i) => [
              i.code,
              i.bonus_days,
              i.subscription_tier,
              i.status,
              i.used_by ?? '—',
              formatDate(i.created_at),
            ])}
            empty="Нет инвайтов"
          />
          {user.invites_used.length > 0 && (
            <>
              <h3>Активированные инвайты</h3>
              <Table
                headers={['Код', 'Дней', 'Tier', 'Использован']}
                rows={user.invites_used.map((i) => [
                  i.code,
                  i.bonus_days,
                  i.subscription_tier,
                  formatDate(i.used_at),
                ])}
                empty=""
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Table({
  headers,
  rows,
  empty,
}: {
  headers: string[];
  rows: React.ReactNode[][];
  empty: string;
}) {
  if (rows.length === 0) {
    return <p style={{ color: 'var(--text-muted)' }}>{empty}</p>;
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
